// Upload caps are read from the backend (GET /config/upload-limits), never
// hardcoded in UI copy. The defaults below only apply until that fetch lands
// (or if it fails); the server re-checks every upload regardless.
import { useEffect, useState } from 'react';
import { configApi } from '../api/examApi';

const limits = { uploadMb: 5, photoMb: 2 };
let loadPromise = null;
const listeners = new Set();

export function loadUploadLimits() {
  if (!loadPromise) {
    loadPromise = configApi
      .getUploadLimits()
      .then((res) => {
        limits.uploadMb = Number(res.data.max_upload_size_mb) || limits.uploadMb;
        limits.photoMb = Number(res.data.max_photo_size_mb) || limits.photoMb;
        listeners.forEach((fn) => fn());
      })
      .catch(() => {
        loadPromise = null; // allow a retry on next use
      });
  }
  return loadPromise;
}

export const getMaxUploadMb = () => limits.uploadMb;
export const getMaxPhotoMb = () => limits.photoMb;

// Re-renders the caller once the real limits have loaded.
export function useUploadLimits() {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    loadUploadLimits();
    return () => listeners.delete(fn);
  }, []);
  return { uploadMb: limits.uploadMb, photoMb: limits.photoMb };
}

const sizeMb = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

// Returns an error message if the file exceeds the shared upload cap, else null.
export function checkFileSize(file) {
  if (file && file.size > limits.uploadMb * 1024 * 1024) {
    return `This file is ${sizeMb(file.size)} MB; the limit is ${limits.uploadMb} MB. Please compress it and upload again.`;
  }
  return null;
}

export const isPdf = (file) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');

// PDF-only check for every PDF upload surface (answer sheets, question
// papers, corrected sheets). UX only — the server re-checks magic bytes.
export function checkPdfFile(file, noun = 'File') {
  if (!file) return null;
  if (!isPdf(file)) return `${noun} must be a PDF file`;
  return checkFileSize(file);
}

// Exam answer sheets are PDF-only (locked rule).
export function checkSubmissionFile(file) {
  return checkPdfFile(file, 'Answer sheets');
}

// Returns an error message if the file exceeds the photo-specific cap, else null.
export function checkPhotoSize(file) {
  if (file && file.size > limits.photoMb * 1024 * 1024) {
    return `This photo is ${sizeMb(file.size)} MB; the limit is ${limits.photoMb} MB. Please use a smaller image.`;
  }
  return null;
}
